import { LinkButton, Stack, Inline, breakpoints } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { PRICE } from '../../../../../constants/searchFiltersConstants';
import PriceSliderFilter from '../PriceSliderFilter';
import TravelTime from '../TravelTime';
import DepartureArrivalTime from '../DepartureArrivalTime';
import { RESULT_PAGE_VERSION_3 } from '../../../constants/resultConstants';
import StopoverTypes from '../StopoverTypes';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import { useResultPageContext } from '../../v3/contexts/ResultPageContext';
import NumberOfStops from './NumberOfStops';
import Airlines from './Airlines';
import CheckedBaggage from './CheckedBaggage';
import type { Filters } from './MobileFilters';

const stackStyles = css`
  padding-bottom: 64px;

  & section {
    border-color: ${etiColors.grayScale[4.5]};
    border-style: solid;
    border-width: 1px 0 0;
  }
`;

const fullWidthFilters = css`
  &:nth-of-type(1) {
    border: none;
  }

  @media (min-width: ${breakpoints._970}) {
    flex: 0 0 100%;
    max-width: 100%;
    padding: 0;
    width: 100%;

    &:first-child > header {
      padding-top: 0;
    }
  }
`;

const clearStyles = css`
  flex: 1 0 100%;
  font-size: 0.875rem;

  @media (min-width: ${breakpoints._970}) {
    font-size: 0.875rem;
  }
`;

const filteredFlightsCountStyles = css`
  font-size: 0.875rem;
  height: 26px;
  white-space: nowrap;
`;

const clearButtonPlaceholderStyles = css`
  flex: 1 0 100%;
`;

const skipLinkStyles = css`
  height: 1px;
  left: -10000px;
  overflow: hidden;
  position: absolute;
  top: auto;
  width: 1px;

  &:focus {
    height: auto;
    padding-bottom: 8px;
    position: static;
    width: auto;
  }
`;

interface DesktopSidebarFiltersProps {
  onClear: () => void;
  onFilter: (filterOptions: { [key: string]: string[] | null | string }) => void;
  selectedFilters?: Filters;
}

const DesktopSidebarFilters = ({
  onClear,
  onFilter,
  selectedFilters,
}: DesktopSidebarFiltersProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { searchData } = useResultPageContext();
  const filteredFlightsCount = searchData?.filteredFlightsCount ?? 0;
  const flightsCount = searchData?.flightsCount ?? 0;
  const resultSetMetaData = searchData?.resultSetMetaData;
  const searchRoutes = searchData?.routes ?? [];
  const hasSelectedFilters = selectedFilters && Object.values(selectedFilters).some(Boolean);
  const searchFormStopoverEnabled = p('Searchform.Stopover.Enabled');
  const resultPageVersion = String(p('Result.Page.Version'));
  const isAirlinesEnabled = p('Result.Filter.Airlines.Enabled');
  const isStopoverEnabled =
    searchFormStopoverEnabled && resultPageVersion === RESULT_PAGE_VERSION_3;
  const isCheckedBaggageEnabled = p('Result.Filter.CheckedBaggage.Enabled');

  const isResultPageQuickSortEnabled = p('Result.Page.QuickFilters.Enabled');

  const handleLinkButtonClick = () => {
    if (isResultPageQuickSortEnabled) {
      const quickSortVersion = p('Result.Page.QuickSort.Version');
      const firstQuickSortButton = document.getElementById(
        `first-quick-sort-button-v${quickSortVersion}`,
      );

      firstQuickSortButton?.focus();
    }
  };

  return (
    <Stack className={stackStyles} data-testid="resultPage-filtersContainer" spacing={16}>
      <LinkButton className={skipLinkStyles} onClick={handleLinkButtonClick}>
        {t('ScreenReader.Result.Filters.SkipLink.Text')}
      </LinkButton>
      <h2 className={screenReadersOnlyStyles}>
        {t('ScreenReader.Result.Filters.Heading.AriaLabel')}
      </h2>
      <Inline data-testid="resultPage-sidebar-number-of-flights-shown" spacing={0}>
        <span className={filteredFlightsCountStyles}>
          {t(
            'Result.SearchFilters.ShowingFilteredResults.Label',
            filteredFlightsCount,
            flightsCount,
          )}
        </span>
        {hasSelectedFilters ? (
          <LinkButton
            className={clearStyles}
            data-testid="resultPage-filterHeader-allFilterResetButton-button"
            onClick={onClear}
          >
            {t('Result.SearchFilters.ClearFilters.Button')}
          </LinkButton>
        ) : (
          <div className={clearButtonPlaceholderStyles} />
        )}
      </Inline>
      {isStopoverEnabled && (
        <StopoverTypes
          className={fullWidthFilters}
          isInitiallyExpanded
          isToggleable
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      {!isStopoverEnabled && (
        <NumberOfStops
          className={fullWidthFilters}
          isInitiallyExpanded
          isToggleable
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      {isCheckedBaggageEnabled && (
        <CheckedBaggage
          className={fullWidthFilters}
          isInitiallyExpanded
          isToggleable
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      <PriceSliderFilter
        className={fullWidthFilters}
        isInitiallyExpanded
        isToggleable
        onFilter={onFilter}
        range={resultSetMetaData?.priceRange}
        selectedValues={selectedFilters && selectedFilters[PRICE]}
      />
      {isAirlinesEnabled && (
        <Airlines
          className={fullWidthFilters}
          isInitiallyExpanded
          isToggleable
          marketingCarriers={resultSetMetaData?.marketingCarriers ?? []}
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      {searchRoutes.map((route, i) => (
        <DepartureArrivalTime
          className={fullWidthFilters}
          destinationName={route?.destination.cityName ?? ''}
          index={i}
          isInitiallyExpanded
          isToggleable
          key={`${route?.departureAt}-${route?.origin.cityName}-${route?.destination.cityName}`}
          onFilter={onFilter}
          originName={route?.origin.cityName ?? ''}
          selectedFilters={selectedFilters}
        />
      ))}
      <TravelTime
        className={fullWidthFilters}
        isInitiallyExpanded
        isToggleable
        onFilter={onFilter}
        selectedFilters={selectedFilters}
        travelTimeRange={resultSetMetaData?.travelTimeRange}
      />
    </Stack>
  );
};

export default DesktopSidebarFilters;
